import { useAuth } from '@shop/api-client'
import { BRAND } from '@shop/shared'
import { Spinner } from '@shop/ui'
import Login from './screens/Login'
import Counter from './screens/Counter'
import OfflineCounter from './offline/OfflineCounter'
import { useAutoSync } from './offline/queue'

/**
 * قشرةُ نقطة البيع.
 *
 * <b>وثلاثُ حالاتٍ لا حالتان</b>، كما في الإدارة: «لم يُسأل الخادمُ بعد» ليست
 * «لا جلسة». ولو سُوّي بينهما لَومض بابُ الدخول في وجه الكاشير في كلّ تحديثِ
 * صفحة — وجهازُ الكاشير يُحدَّث كثيرًا.
 *
 * ⚠ <b>والجلسةُ هنا غيرُ جلسة الإدارة، وهذا مقصود</b> (قرار المالك ٢٠٢٦-٠٩-١٦):
 *   كوكيّاتُ الجلسة تحمل بادئةَ <c>__Host-</c>، فيرفض المتصفّحُ أن تُضبط
 *   بـ<c>Domain</c> — وتصير مقصورةً على المضيف الذي أصدرها. فمن دخل
 *   <c>app.</c> لا يكون داخلًا في <c>pos.</c>.
 *
 *   والحسابُ واحدٌ والصلاحيّاتُ واحدة: الفرقُ في الجلسة لا في الهويّة.
 */
export default function App() {
  const { session } = useAuth()

  // ما جرى دون اتّصال يُرحَّل وحدَه حين يعود النت — باسم من سجّله (`0052`).
  useAutoSync(session?.me.userId ?? null, session ? !session.offline : false)

  if (session === undefined) {
    return (
      <div className="grid min-h-dvh place-content-center">
        <Spinner label="يُستأنف…" />
      </div>
    )
  }

  if (session === null) {
    return <Login />
  }

  // ⚠ ولا نموذجَ تغييرِ كلمةٍ هنا — **عمدًا لا نقصًا**.
  //
  //   تغييرُ الكلمة شاشةٌ قائمةٌ ومختبَرةٌ في الإدارة. ونسخُها هنا يُنتج مسارين
  //   لفعلٍ واحد، فيُصلَح أحدهما يومًا ويبقى الآخر — وهو بالضبط ما منعه المالك:
  //   لا تكرارَ بين `app` و`pos`.
  //
  //   والكاشيرُ يغيّرها من الإدارة مرّةً واحدةً في عمر حسابه، ثمّ لا يعود.
  // ⚠ **دون اتّصال قبل كلمة المرور المؤقّتة**: الجلسةُ من نسخة الجهاز لا تعرف
  //   إلا أنّ صاحبها دخل من قبل — والصندوقُ يبيع.
  if (session.offline) {
    return <OfflineCounter />
  }

  if (session.mustChangePassword) {
    return <MustChangePassword />
  }

  return <Counter />
}

/**
 * عنوانُ تطبيق الإدارة — <b>مشتقٌّ من العنوان الحاليّ لا مكتوبٌ ثابتًا</b>.
 *
 * فبيئةُ التطوير على <c>localhost</c>، والإنتاجُ على <c>pos.</c>، والمعاينةُ
 * قد تكون على ثالث. وعنوانٌ ثابتٌ يعمل في واحدةٍ ويقود إلى لا شيءٍ في الباقي.
 */
function adminUrl(): string | null {
  const host = window.location.hostname

  if (host.startsWith('pos.')) {
    return `${window.location.protocol}//app.${host.slice(4)}/`
  }

  return null
}

function MustChangePassword() {
  const { logout } = useAuth()
  const url = adminUrl()

  return (
    <div className="mx-auto grid min-h-dvh max-w-md content-center gap-4 p-6 text-center">
      <h1 className="text-xl font-bold">كلمةُ المرور مؤقّتة</h1>

      <p className="text-sm text-ink-soft">
        غيّرها من تطبيق الإدارة ثمّ عُد إلى نقطة البيع. وهي مرّةٌ واحدةٌ في عمر
        الحساب.
      </p>

      {url ? (
        <a
          className="rounded-lg bg-brand px-4 py-3 font-semibold text-white"
          href={url}
          rel="noopener"
          target="_blank"
        >
          افتح تطبيق الإدارة
        </a>
      ) : (
        <p className="text-sm text-ink-soft">افتح تطبيقَ الإدارة وغيّرها من هناك.</p>
      )}

      <button className="text-sm text-ink-soft underline" onClick={() => void logout()} type="button">
        خروج
      </button>

      <p className="text-xs text-ink-soft">{BRAND.nameAr}</p>
    </div>
  )
}
